import React, { useState } from 'react';
import './DowngradeModal.css';

const DowngradeModal = ({ isOpen, onClose, onConfirm, currentPlan, targetPlan, planPricing }) => {
  const [selectedPlan, setSelectedPlan] = useState(targetPlan || null);
  
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(selectedPlan);
    onClose();
  };

  const getPlanDisplayName = (plan) => {
    const planNames = {
      'free': 'Free',
      'solo': 'Solo',
      'pro': 'Pro'
    };
    return planNames[plan] || plan;
  };

  const getPlanPrice = (plan) => {
    if (plan === 'free') return '$0';
    return planPricing?.[plan]?.monthly ? `$${planPricing[plan].monthly}` : 'Contact Sales';
  };

  const getPlanFeatures = (plan) => {
    switch (plan) {
      case 'free':
        return ['Basic code review', 'Limited daily reviews'];
      case 'solo':
        return ['10 code reviews per month', 'AI-powered analysis', 'GitHub PR reviews', 'Review history'];
      case 'pro':
        return ['50 code reviews per month', 'AI-powered analysis', 'GitHub PR reviews', 'Review history'];
      default:
        return [];
    }
  };

  // Get available downgrade options (plans lower than current)
  const getAvailablePlans = () => {
    const planHierarchy = ['free', 'solo', 'pro'];
    const currentIndex = planHierarchy.indexOf(currentPlan);
    return planHierarchy.slice(0, currentIndex);
  };

  const availablePlans = getAvailablePlans();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-icon">
            <span className="warning-icon">⚠️</span>
          </div>
          <h2 className="modal-title">Choose Plan to Downgrade To</h2>
          <button className="modal-close" onClick={onClose}>
            <span>×</span>
          </button>
        </div>

        <div className="modal-body">
          <div className="downgrade-info">
            <p className="downgrade-description">
              You're currently on the <strong>{getPlanDisplayName(currentPlan)}</strong> plan. 
              Select a plan below to downgrade to:
            </p>
            
            <div className="plan-selection">
              {availablePlans.map((plan) => (
                <div 
                  key={plan}
                  className={`plan-card selectable-plan ${selectedPlan === plan ? 'selected' : ''}`}
                  onClick={() => setSelectedPlan(plan)}
                >
                  <div className="plan-header">
                    <h3>{getPlanDisplayName(plan)} Plan</h3>
                    <span className="plan-price">{getPlanPrice(plan)}/month</span>
                  </div>
                  <div className="plan-features">
                    {getPlanFeatures(plan).map((feature, index) => (
                      <div key={index} className="feature-item">
                        <span className="feature-icon">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {selectedPlan && (
              <div className="plan-comparison">
                <div className="plan-card current-plan">
                  <div className="plan-header">
                    <h3>{getPlanDisplayName(currentPlan)} Plan</h3>
                    <span className="plan-price">{getPlanPrice(currentPlan)}/month</span>
                  </div>
                  <div className="plan-features">
                    {getPlanFeatures(currentPlan).map((feature, index) => (
                      <div key={index} className="feature-item">
                        <span className="feature-icon">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="plan-arrow">
                  <span>→</span>
                </div>

                <div className="plan-card target-plan">
                  <div className="plan-header">
                    <h3>{getPlanDisplayName(selectedPlan)} Plan</h3>
                    <span className="plan-price">{getPlanPrice(selectedPlan)}/month</span>
                  </div>
                  <div className="plan-features">
                    {getPlanFeatures(selectedPlan).map((feature, index) => (
                      <div key={index} className="feature-item">
                        <span className="feature-icon">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="downgrade-warnings">
              <div className="warning-item">
                <span className="warning-icon-small">⚠️</span>
                <span>You may lose access to premium features</span>
              </div>
              <div className="warning-item">
                <span className="warning-icon-small">📅</span>
                <span>Changes take effect at your next billing cycle</span>
              </div>
              <div className="warning-item">
                <span className="warning-icon-small">💰</span>
                <span>You'll be charged the new plan rate</span>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Keep Current Plan
          </button>
          <button 
            className="btn-confirm" 
            onClick={handleConfirm}
            disabled={!selectedPlan}
          >
            Confirm Downgrade to {getPlanDisplayName(selectedPlan)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DowngradeModal;
